import { createTheme } from "@mui/material/styles";
import { colors } from "@polifonia/theme/colors";

declare module "@mui/material" {
  interface AlertPropsColorOverrides {
    plain: true;
  }
}

const theme = {
  colors,
  shape: {
    borderRadius: "4px",
  },
  shadows: [
    `0 6px 16px 0 rgba(0, 0, 0, 0.08),
    0 3px 6px -4px rgba(0, 0, 0, 0.12),
    0 9px 28px 8px rgba(0, 0, 0, 0.05)`,
  ],
} as const;

type Theme = typeof theme;

const defaultMuiShadows = createTheme().shadows;

const muiTheme = createTheme({
  typography: {
    // We configure the font family in the global CSS by languages
    fontFamily: "inherit",
  },
  palette: {
    primary: {
      main: theme.colors.primary,
    },
  },
  shadows: [...defaultMuiShadows.fill(theme.shadows[0], 0)],
  components: {
    MuiTextField: {
      styleOverrides: {
        root: {
          ".MuiInputBase-root": {
            "& .MuiInputBase-input": {
              fontSize: "0.875rem",
            },
          },
          "& .MuiInputBase-multiline": {
            padding: 0,
          },
        },
      },
    },
    MuiAlertTitle: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          padding: "8px 12px",
          boxShadow: "none",
          minWidth: "320px",
          maxWidth: "100%",
          borderRadius: theme.shape.borderRadius,
          ...(ownerState.severity === "plain" && {
            backgroundColor:
              ownerState.variant === "outlined"
                ? "transparent"
                : theme.colors.neutral020,
            color: theme.colors.neutral090,
          }),
          "& .MuiAlert-icon": {
            fontSize: "16px",
            color: "inherit",
          },
          "& .MuiAlert-action": {
            margin: "0 0 0 auto",
            padding: 0,
          },
        }),
        outlined: {
          padding: 0,
          border: "none",
        },
        // outlinedInfo is not 1:1 mapped with out design system so we don't override it here. Instead, we override it in InfoBox where the Alert is used.
        outlinedSuccess: {
          color: theme.colors.staticFgPositive,
          "& .MuiAlert-icon": {
            color: theme.colors.staticFgPositive,
          },
        },
        outlinedWarning: {
          color: theme.colors.staticFgAttentive,
          "& .MuiAlert-icon": {
            color: theme.colors.staticFgAttentive,
          },
        },
        outlinedError: {
          color: theme.colors.staticFgNegative,
          "& .MuiAlert-icon": {
            color: theme.colors.staticFgNegative,
          },
        },
        // standardInfo is not 1:1 mapped with out design system so we don't override it here. Instead, we override it in InfoBox where the Alert is used.
        standardSuccess: {
          backgroundColor: theme.colors.staticBgPositive,
          color: theme.colors.staticFgPositive,
          "& .MuiAlert-icon": {
            color: theme.colors.staticFgPositive,
          },
        },
        standardWarning: {
          backgroundColor: theme.colors.staticBgAttentive,
          color: theme.colors.staticFgAttentive,
          "& .MuiAlert-icon": {
            color: theme.colors.staticFgAttentive,
          },
        },
        standardError: {
          backgroundColor: theme.colors.staticBgNegative,
          color: theme.colors.staticFgNegative,
          "& .MuiAlert-icon": {
            color: theme.colors.staticFgNegative,
          },
        },
        icon: {
          marginRight: "4px",
          padding: "2px",
          svg: {
            fontSize: "1rem",
          },
        },
        message: {
          padding: "0",
        },
      },
    },
    MuiButtonBase: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          // I don't know why global typography fontFamily doesn't work. I need this additional line to override it.
          fontFamily: "inherit",
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
          lineHeight: 1.42857,
          margin: "4px 0 0",
          color: theme.colors.neutral070,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "20px",
          height: "24px",
        },
        label: {
          padding: "2px 8px",
        },
        deleteIcon: {
          fontSize: "14px",
          color: "currentcolor",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: theme.shape.borderRadius,
          margin: "0 4px",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          padding: "7px",
          fontSize: "0.875rem",
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: "36px",
        },
        indicator: {
          height: "3px",
          borderRadius: "3px",
        },
        flexContainer: {
          gap: "12px",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          padding: "8px 12px",
          minHeight: "33px",
          textTransform: "none",
          color: theme.colors.tabsFgInactive,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: theme.shape.borderRadius,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "12px 8px",
        },
      },
    },
  },
});

export { muiTheme, theme };
export type { Theme };
